// Shifts a week-grouping (from groupScheduleByWeek / groupBracketByWeek) so its weeks read as
// season ordinals starting at 1. `baseWeek` is the first week present in the grouping; groups
// are returned unchanged when it is already 1 (or when the grouping is empty).
export function renumberWeekGroups (groups, baseWeek) {
  if (!groups.length || baseWeek <= 1) return groups;
  return groups.map(group => ({ ...group, week: group.week - baseWeek + 1 }));
}

// Groups round-robin schedule events by week, attaching the resolved home/away squads and the
// list of squads with a bye that week. Bracket events are excluded. Events are expected to be
// in schedule order; weeks are returned ascending.
export function groupScheduleByWeek ({ events, squads }) {
  const byWeek = new Map();
  for (const event of events || []) {
    if (event.bracketSlot) continue;
    if (!byWeek.has(event.week)) byWeek.set(event.week, []);
    byWeek.get(event.week).push({
      ...event,
      homeSquad: squads.find(s => s.id === event.homeSquadId),
      awaySquad: squads.find(s => s.id === event.awaySquadId)
    });
  }
  return [...byWeek.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([week, races]) => {
      races.sort((a, b) => (a.date?.getTime() || 0) - (b.date?.getTime() || 0));
      const playing = new Set();
      for (const race of races) {
        playing.add(race.homeSquadId);
        playing.add(race.awaySquadId);
      }
      const byes = squads.filter(squad => !playing.has(squad.id));
      return { week, races, byes };
    });
}

// Groups bracket schedule events by their resolved week (the scheduled week once finalists are
// known, else the nominal event week). Attaches the scheduled placement, its resolved location,
// and whether the slot is a structural bye (no away seed, or a single known squad).
export function groupBracketByWeek ({ events, slotSquads = {}, schedule = {}, locations }) {
  const byWeek = new Map();
  for (const event of events || []) {
    if (!event.bracketSlot) continue;
    const scheduled = schedule[event.bracketSlot];
    const week = scheduled?.week ?? event.week;
    const slotIds = slotSquads[event.bracketSlot];
    const isBye = event.bracketSlot !== "THIRD" && (event.awaySeed == null || (slotIds && slotIds.length === 1));
    if (!byWeek.has(week)) byWeek.set(week, []);
    byWeek.get(week).push({
      ...event,
      isBye,
      scheduled,
      location: scheduled?.locationId ? locations.find(l => l.id === scheduled.locationId) : null
    });
  }
  return [...byWeek.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([week, bracketEvents]) => ({ week, bracketEvents, races: [], byes: [] }));
}